import clone from "lodash/clone";
import forOwn from "lodash/forOwn";
import get from "lodash/get";
import React from "react";
import { withTranslation } from "react-i18next";
import submit from "submit-form";
import { RillContext } from "../../rill/RillReact/share/base";
import { getExerciseNamesList } from "../../utils/progression-filters";
import Modal from "../modal-new";
import CustomFilters from "./duplicate-workout-components/CustomFilters";
import DuplicateModalFooter from "./duplicate-workout-components/DuplicateModalFooter";
import LoadProgression from "./duplicate-workout-components/LoadProgression";
// Added for optimization tweaks, so as to not have as many arrow functions in render
import ProgressionCheckbox from "./duplicate-workout-components/ProgressionCheckbox";
import ProgressionInput from "./duplicate-workout-components/ProgressionInput";

class DuplicateWeekModal extends React.Component {
  constructor(props, context) {
    super(props, context);
    this.state = {
      data: {
        cycle: get(context.locals, "params.cycleId", ""),
        program: get(context.locals, "query.program", ""),
        from: props.calendarMode
          ? props.from
          : get(context.locals, "query.week", ""),
        to: [],
      },
      progressions: {},
      checkedFields: {
        loadProgression: false,
        rpeProgression: false,
        repsProgression: false,
        setsProgression: false,
      },
      checkedFilters: {
        allFilter: true,
        primaryFilter: false,
        secondaryFilter: false,
        assistantFilter: false,
        customFilter: [],
      },
      loadSwitch: {
        percent: true,
        units: context.locals.activeUser.units === "imperial" ? "lb" : "kg",
      },
      viewFilters: false,
    };

    this.exercisesList = getExerciseNamesList(
      context.locals.editExercises || context.locals.activeExercises,
      context.locals.query
    );

    this.handleCheck = this.handleCheck.bind(this);
    this.handleFilters = this.handleFilters.bind(this);
    this.handleProgressionInput = this.handleProgressionInput.bind(this);
    this.toggleSelection = this.toggleSelection.bind(this);
    this.handleLoadSwitch = this.handleLoadSwitch.bind(this);
    this.updateCustomFilter = this.updateCustomFilter.bind(this);
    this.handleClearAll = this.handleClearAll.bind(this);
  }

  handleDuplicate(e) {
    e.preventDefault();
    const { progressions, checkedFilters, loadSwitch } = this.state;
    const { calendarMode = false } = this.props;
    const data = { ...this.state.data };

    if (!calendarMode) {
      const weeksWithErrors = this.validateWorkoutLengthByWeek(data);

      if (weeksWithErrors.length > 0) {
        // set the error message and add it to the data sent to the submit function
        //if no data.error defined, no validation message appears
        const errorMessage = this.props.t(
          "duplicateWeek.validationError",
          {
            weeks: weeksWithErrors
              .map((w) => parseInt(w[0].week))
              .join(", "),
          }
        );
        data.error = errorMessage;
      }
    }

    data.to = data.to.map((week) => {
      const result = {
        week,
        progression: {
          progressionLoad: progressions[`${week}_progression_load`],
          progressionRpe: progressions[`${week}_progression_rpe`],
          progressionSets: progressions[`${week}_progression_sets`],
          progressionReps: progressions[`${week}_progression_reps`],
          progressionFilters: checkedFilters,
          loadSwitch,
        },
      };
      if (calendarMode)
        result.startDate = this.props.weeks[week].format("YYYY-MM-DD");
      return result;
    });

    // Ensure each object in the "to" array has both "week" and "progression" properties
    data.to = data.to.filter((item) => item.week && item.progression);

    const calendarBody = {
      calendarMode,
      from: data.from,
      to: data.to,
      proxyAthlete: this.props.proxyAthlete,
    };

    if (this.context.locals.activeUser.organizationId) {
      data.user = this.context.locals.activeUserToRequest._id;
    }

    submit("/actions/cycle/program/duplicateWeek/", {
      method: "POST",
      body: calendarMode ? calendarBody : data,
    });
  }

  /**
   * Validates if the source week has the same amount of workouts that every destination week
   * @param data
   * @returns {Array.<*>} an array with the invalid destination weeks. If no error, returns an empty array
   */
  validateWorkoutLengthByWeek(data) {
    let sourceWeek = null,
      destinationWeeks = [];
    forOwn(this.props.weeks, (week, weekNumber) => {
      if (weekNumber == data.from) {
        sourceWeek = week;
      } else {
        data.to.forEach((toWeek) => {
          if (weekNumber == parseInt(toWeek) + 1) {
            destinationWeeks.push(week);
          }
        });
      }
    });

    const sourceLength = sourceWeek.length;
    const weeksWithErrors = destinationWeeks.filter((destWeek) => {
      return destWeek.length !== sourceLength;
    });

    return weeksWithErrors;
  }

  shouldDisable(week, weeKHasExercises) {
    // if week === from
    // week contains exercises
    const { from } = this.state.data;
    return Boolean(from == week || weeKHasExercises);
  }

  toggleSelection({ target: { value } }) {
    this.setState((prevState) => {
      const { data } = prevState;
      const { to } = data;

      const index = to.indexOf(value);
      let newTo;
      if (index > -1) {
        newTo = [...to.slice(0, index), ...to.slice(index + 1)];
      } else {
        newTo = [...to, value];
      }

      return {
        data: {
          ...data,
          to: newTo,
        },
      };
    });
  }

  handleProgressionInput({ target: { name, value } }) {
    const {
      locals: { activeUser, activeUserToRequest },
    } = this.context;
    const intensityMeasure = get(
      activeUserToRequest,
      "intensityMeasure",
      "rpe"
    );
    const progressions = clone(this.state.progressions);
    progressions[name] = Number(value);
    if (name.includes("rpe") && intensityMeasure === "rir") {
      progressions[name] = progressions[name] * -1;
    }
    this.setState(() => ({ progressions }));
  }

  handleCheck({ target: { name, checked } }) {
    this.setState((prevState) => {
      const checkedFields = { ...prevState.checkedFields };
      checkedFields[name] = checked;
      return { checkedFields };
    });
  }

  handleFilters({ target: { name, checked } }) {
    if (name === "allFilter") {
      this.setState({
        checkedFilters: {
          allFilter: true,
          primaryFilter: false,
          secondaryFilter: false,
          assistantFilter: false,
          customFilter: [],
        },
      });
    } else if (name === "customFilter") {
      this.setState({
        viewFilters: true,
      });
    } else {
      this.setState((prevState) => {
        const checkedFilters = { ...prevState.checkedFilters };
        checkedFilters.allFilter = false;
        checkedFilters.customFilter = [];
        checkedFilters[name] = checked;
        return { checkedFilters };
      });
    }
  }

  updateCustomFilter(action, data) {
    switch (action) {
      case "check":
        // Custom filter checkbox checked, update array
        return this.setState((prevState) => {
          const checkedFilters = {
            ...prevState.checkedFilters,
            customFilter: data,
          };
          return { checkedFilters };
        });
      case "discard":
        // "Discard", viewFilters off and clear customFilter
        return this.setState((prevState) => {
          const checkedFilters = {
            ...prevState.checkedFilters,
            customFilter: [],
          };
          return { checkedFilters, viewFilters: false };
        });
      default:
        // "Save" case, viewFilters off, all filters off except customFilter
        return this.setState((prevState) => {
          const checkedFilters = {
            ...prevState.checkedFilters,
            allFilter: false,
            primaryFilter: false,
            secondaryFilter: false,
            assistantFilter: false,
          };
          return { checkedFilters, viewFilters: false };
        });
    }
  }

  handleLoadSwitch(e) {
    e.preventDefault();

    this.setState(({ loadSwitch }) => ({
      loadSwitch: { ...loadSwitch, percent: !loadSwitch.percent },
    }));
  }

  handleClearAll(e) {
    e.preventDefault();
    this.setState(({ data }) => ({
      progressions: {},
      checkedFields: {
        loadProgression: false,
        rpeProgression: false,
        repsProgression: false,
        setsProgression: false,
      },
      checkedFilters: {
        allFilter: true,
        primaryFilter: false,
        secondaryFilter: false,
        assistantFilter: false,
        customFilter: [],
      },
      data: { ...data, to: [] },
    }));
    document.getElementById("duplicate-week-form")?.reset?.();
  }

  render() {
    const { t, weeksOrder, duplicateCheck } = this.props;
    const { data, checkedFields, checkedFilters, loadSwitch, progressions } =
      this.state;
    const { from } = data;
    const {
      loadProgression,
      rpeProgression,
      setsProgression,
      repsProgression,
    } = checkedFields;
    const {
      allFilter,
      primaryFilter,
      secondaryFilter,
      assistantFilter,
      customFilter,
    } = checkedFilters;
    const {
      locals: { activeUserToRequest },
    } = this.context;
    const intensityMeasureRaw = get(
      activeUserToRequest,
      "intensityMeasure",
      "rpe"
    );
    const intensityMeasure = intensityMeasureRaw === "rpe" ? "RPE" : "RiR";

    const loadLabel = loadSwitch.percent ? "%" : loadSwitch.units;
    const disabledProgression =
      !loadProgression &&
      !rpeProgression &&
      !repsProgression &&
      !setsProgression;

    if (this.state.viewFilters) {
      return (
        <CustomFilters
          exercises={this.exercisesList}
          customFilter={customFilter}
          updateCustomFilter={this.updateCustomFilter}
        />
      );
    }

    return (
      <form
        onSubmit={(e) => this.handleDuplicate(e)}
        data-noreset
        id="duplicate-week-form"
      >
        <Modal
          className="duplicate-week-modal"
          toClear={["session", "week"]}
          title={t("duplicateWeek.title", { week: from })}
        >
          <div className="body">
            <div className="progression-toggle">
              {(!assistantFilter || secondaryFilter || primaryFilter) && (
                <LoadProgression
                  handleCheck={this.handleCheck}
                  handleLoadSwitch={this.handleLoadSwitch}
                  loadSwitch={loadSwitch}
                  checked={loadProgression}
                />
              )}

              <ProgressionCheckbox
                name="repsProgression"
                onChange={this.handleCheck}
                checked={repsProgression}
              >
                {t("duplicateWorkout.addProgressionsToReps")}
              </ProgressionCheckbox>

              <ProgressionCheckbox
                name="rpeProgression"
                onChange={this.handleCheck}
                checked={rpeProgression}
              >
                {t("duplicateWorkout.addProgressionsToIntensity", {
                  intensityMeasure,
                })}
              </ProgressionCheckbox>

              <ProgressionCheckbox
                name="setsProgression"
                onChange={this.handleCheck}
                checked={setsProgression}
              >
                {t("duplicateWorkout.addProgressionsToSets")}
              </ProgressionCheckbox>
            </div>
            <div
              className={`progression-filter ${
                disabledProgression ? " disabled" : ""
              }`}
            >
              <span>{t("duplicateWorkout.exerciseTypeProgressionFilter")}</span>
              <ul>
                <ProgressionCheckbox
                  name="allFilter"
                  checked={allFilter}
                  onChange={this.handleFilters}
                  disabled={disabledProgression}
                >
                  {t("duplicateWorkout.all")}
                </ProgressionCheckbox>
                <ProgressionCheckbox
                  name="primaryFilter"
                  checked={primaryFilter}
                  onChange={this.handleFilters}
                  disabled={disabledProgression}
                >
                  {t("duplicateWorkout.primaryLift")}
                </ProgressionCheckbox>
                <ProgressionCheckbox
                  name="secondaryFilter"
                  checked={secondaryFilter}
                  onChange={this.handleFilters}
                  disabled={disabledProgression}
                >
                  {t("duplicateWorkout.variations")}
                </ProgressionCheckbox>
                <ProgressionCheckbox
                  name="assistantFilter"
                  checked={assistantFilter}
                  onChange={this.handleFilters}
                  disabled={disabledProgression}
                >
                  {t("duplicateWorkout.accessories")}
                </ProgressionCheckbox>
                <ProgressionCheckbox
                  name="customFilter"
                  checked={customFilter.length > 0}
                  onChange={this.handleFilters}
                  disabled={disabledProgression}
                >
                  {t("duplicateWorkout.customFilterCount", {
                    count: customFilter.length,
                  })}
                </ProgressionCheckbox>
              </ul>
            </div>

            <div className="progressions-wrapper">
              {!disabledProgression && (
                <div className="progression-workout">
                  {(!assistantFilter || secondaryFilter || primaryFilter) && (
                    <p className={`p2 ${loadProgression}`}>
                      {t("duplicateWorkout.loadLabel", { loadLabel })}{" "}
                    </p>
                  )}
                  <p className={`p2 ${rpeProgression}`}>
                    {t("duplicateWorkout.intensityIncr", {
                      intensityMeasure,
                    })}
                  </p>
                  <p className={`p2 ${repsProgression}`}>
                    {t("duplicateWorkout.reps")}
                  </p>
                  <p className={`p2 ${setsProgression}`}>
                    {t("duplicateWorkout.sets")}
                  </p>
                </div>
              )}
              <div className="progression-weeks">
                {duplicateCheck.map((weekHasExercises, week) => {
                  const disabled = this.shouldDisable(
                    week + 1,
                    weekHasExercises
                  );
                  return (
                    <div key={week}>
                      <ProgressionCheckbox
                        value={week}
                        disabled={disabled}
                        onChange={this.toggleSelection}
                        checked={data.to.some((value) => value == week)}
                      />
                      <p className="whitespace-nowrap s1">
                        {this.props.calendarMode
                          ? weeksOrder[week]
                          : t("duplicateWorkout.week", {
                              week: weeksOrder[week],
                            })}
                      </p>

                      {(!assistantFilter ||
                        secondaryFilter ||
                        primaryFilter) && (
                        <ProgressionInput
                          disabled={disabled}
                          step="0.5"
                          placeholder={t("duplicateWorkout.loadPlaceholder", {
                            loadLabel: loadLabel,
                          })}
                          name={`${week}_progression_load`}
                          onInput={this.handleProgressionInput}
                          progressions={progressions}
                          unchecked={!loadProgression}
                        />
                      )}
                      <ProgressionInput
                        disabled={disabled}
                        name={`${week}_progression_rpe`}
                        onInput={this.handleProgressionInput}
                        isRpe
                        progressions={progressions}
                        unchecked={!rpeProgression}
                        intensityMeasure={intensityMeasureRaw}
                      />
                      <ProgressionInput
                        disabled={disabled}
                        step="1"
                        placeholder={t("duplicateWorkout.reps")}
                        name={`${week}_progression_reps`}
                        onInput={this.handleProgressionInput}
                        progressions={progressions}
                        unchecked={!repsProgression}
                      />
                      <ProgressionInput
                        disabled={disabled}
                        step="1"
                        placeholder={t("duplicateWorkout.sets")}
                        name={`${week}_progression_sets`}
                        onInput={this.handleProgressionInput}
                        progressions={progressions}
                        unchecked={!setsProgression}
                      />
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
          <DuplicateModalFooter
            onClear={this.handleClearAll}
            disabled={data.to.length === 0}
            btnText={t("duplicateWorkout.duplicate")}
          />
        </Modal>
      </form>
    );
  }
}

DuplicateWeekModal.contextType = RillContext;

export default withTranslation("coaching-portal")(DuplicateWeekModal);
